import { Functions } from 'appwrite';
import client from './appwrite';

const functions = new Functions(client);

// Function endpoints
const functionIds = {
  userAuth: 'userAuth',
  classManagement: '68274a1b0004d562903f',
  availabilityManagement: '682749e2002bfb9a7200',
  notifications: '68274a3f0031c188ee43',
  utilities: '682749a2001df431803b'
};

// Helper for calling Appwrite functions
const callFunction = async (functionName, data = {}) => {
  const functionId = functionIds[functionName];

  if (!functionId) {
    const errorMessage = `[Client] Unknown function: ${functionName}`;
    console.error(errorMessage);
    return { success: false, message: errorMessage, errorType: 'ConfigurationError' };
  }

  try {
    console.log(`[Client] Calling function ${functionName} (ID: ${functionId}) with data:`, data);

    const execution = await functions.createExecution(
      functionId,
      JSON.stringify(data)
    );

    console.log(`[Client] Function ${functionName} raw execution object:`, JSON.parse(JSON.stringify(execution)));
    console.log(`[Client] execution.status: ${execution.status}`);
    console.log(`[Client] execution.statusCode: ${execution.statusCode}`);
    console.log(`[Client] execution.response (legacy/raw):`, execution.response);
    console.log(`[Client] execution.responseBody (preferred):`, execution.responseBody);
    console.log(`[Client] execution.stdout:`, execution.stdout);
    console.log(`[Client] execution.stderr:`, execution.stderr);

    const responseDataString = execution.responseBody || execution.response;

    if (execution.status === 'failed' || !responseDataString) {
      const failureMessage = `[Client] Function ${functionName} execution failed or returned empty response. Status: ${execution.status}, StatusCode: ${execution.statusCode}.`;
      console.error(failureMessage);
      if (execution.stderr) {
        console.error(`[Client] Function ${functionName} stderr:`, execution.stderr);
      }
      return {
        success: false,
        message: `Function ${functionName} failed or returned empty. Server stderr: ${execution.stderr || 'N/A'}`,
        details: {
            status: execution.status,
            statusCode: execution.statusCode,
            stderr: execution.stderr
        }
      };
    }

    let parsedClientResponse;
    try {
      parsedClientResponse = JSON.parse(responseDataString);
    } catch (parseError) {
      console.error(`[Client] Error parsing JSON response from ${functionName}:`, parseError);
      console.log('[Client] Raw response string that failed to parse:', responseDataString);
      return {
        success: false,
        message: `Invalid JSON response format from function ${functionName}.`,
        rawResponse: responseDataString,
        parseError: parseError.message
      };
    }

    if (parsedClientResponse.success === false) {
      console.warn(`[Client] Function ${functionName} returned a logical error:`, parsedClientResponse);
      return parsedClientResponse;
    }

    return parsedClientResponse;

  } catch (error) {
    console.error(`[Client] Error during Appwrite function call to ${functionName}:`, error);
    let errorMessage = error.message || 'An unexpected error occurred during the function call.';
    let errorDetails = {};

    if (error.response && typeof error.response === 'object') {
        console.error(`[Client] Appwrite SDK error details:`, error.response);
        errorMessage = error.response.message || errorMessage;
        errorDetails = error.response;
    }
    
    return {
        success: false,
        message: `Error calling function ${functionName}: ${errorMessage}`,
        errorType: error.name || 'FunctionCallError',
        details: errorDetails,
        originalError: error
    };
  }
};

// API functions organized by domain
export const api = {
  // Class related functions
  classes: {
    getAvailable: async (classType) => {
      const response = await callFunction('classManagement', {
        action: 'getAvailableClasses',
        classType
      });
      return response.success ? response.classes : [];
    },

    getDetails: async (classId) => {
      const response = await callFunction('classManagement', {
        action: 'getClassDetails',
        classId
      });
      return response.success ? response.class : null;
    },

    join: (classId, userId, name, email, phone) =>
      callFunction('classManagement', {
        action: 'joinClass',
        classId,
        userId,
        name,
        email,
        phone
      }),

    leave: (classId, userId) =>
      callFunction('classManagement', {
        action: 'leaveClass',
        classId,
        userId
      }),

    create: async (classData) => {
        const response = await callFunction('classManagement', {
            action: 'createClass',
            ...classData
        });
        return response;
    }
  },

  // Class Types management
  classTypes: {
    // Get a single class type by ID
    getById: async (classTypeId) => {
      const response = await callFunction('classManagement', {
        action: 'getClassType',
        classTypeId
      });
      return response.success ? response.classType : null;
    },

    // Get active class types for public use
    getActive: async () => {
      const response = await callFunction('classManagement', {
        action: 'getClassTypes'
      });
      return response.success ? response.classTypes : [];
    },

    // Get all class types for admin (including inactive)
    getAll: async () => {
      const response = await callFunction('classManagement', {
        action: 'getAllClassTypes'
      });
      return response.success ? response.classTypes : [];
    },

    // Create new class type
    create: async (classTypeData) => {
      const response = await callFunction('classManagement', {
        action: 'createClassType',
        ...classTypeData
      });
      return response;
    },

    // Update class type
    update: async (classTypeId, updateData) => {
      const response = await callFunction('classManagement', {
        action: 'updateClassType',
        classTypeId,
        ...updateData
      });
      return response;
    },

    // Delete class type
    delete: async (classTypeId) => {
      const response = await callFunction('classManagement', {
        action: 'deleteClassType',
        classTypeId
      });
      return response;
    }
  },

  // Admin functions
  admin: {
    // Verify if user is admin
    verifyAdmin: async (userId) => {
      const response = await callFunction('userAuth', {
        action: 'verifyAdmin',
        userId
      });
      return response.success ? response.isAdmin : false;
    },

    // Get all classes for admin view
    getAllClasses: async (filters = {}) => {
      const response = await callFunction('classManagement', {
        action: 'getAllClasses',
        ...filters
      });
      return response;
    },

    // Get dashboard stats
    getStats: async () => {
      const response = await callFunction('classManagement', {
        action: 'getClassStats'
      });
      return response.success ? response.stats : null;
    },

    // Update class details
    updateClass: async (classId, updateData) => {
      const response = await callFunction('classManagement', {
        action: 'updateClass',
        classId,
        ...updateData
      });
      return response;
    },

    // Cancel a class
    cancelClass: async (classId, reason = '') => {
      const response = await callFunction('classManagement', {
        action: 'cancelClass',
        classId,
        reason
      });
      return response;
    },

    // Reactivate a cancelled class
    reactivateClass: async (classId) => {
      const response = await callFunction('classManagement', {
        action: 'reactivateClass',
        classId
      });
      return response;
    },

    // Delete a class permanently
    deleteClass: async (classId) => {
      const response = await callFunction('classManagement', {
        action: 'deleteClass',
        classId
      });
      return response;
    },

    // Get enrolled students for a class
    getClassMembers: async (members) => {
      const response = await callFunction('userAuth', {
        action: 'getUsersByClass',
        members
      });
      return response.success ? response.members : [];
    },

    // Admin settings functions
    getSettings: async () => {
      const response = await callFunction('userAuth', {
        action: 'getAdminSettings'
      });
      return response.success ? response.settings : null;
    },

    updateSettings: async (settings) => {
      const response = await callFunction('userAuth', {
        action: 'updateAdminSettings',
        settings
      });
      return response;
    }
  },

  // Availability related functions
  availability: {
    submit: (userId, classTypeId, availabilities, name, email, phone, goalData = {}, isTutoringTrial = false) => {
      const { goal, goal_level, exam_type, exam_level, exam_year_phase, cantonese_level } = goalData;
      
      // For IB exams, concatenate exam_type with exam_level
      let finalExamType = exam_type || null;
      if (exam_type === 'IB' && exam_level) {
        finalExamType = exam_level; // This will be "IB SL" or "IB HL"
      }
      
      return callFunction('availabilityManagement', {
        action: 'submitAvailability',
        userId,
        classTypeId,
        availabilities,
        checkForMatches: true,
        // Add goal data
        goal: goal || null,
        goal_level: goal_level || null,
        exam_type: finalExamType,
        exam_year_phase: exam_year_phase || null,
        cantonese_level: cantonese_level || null
      });
    },

    getUserAvailability: async (userId) => {
      const response = await callFunction('availabilityManagement', {
        action: 'getUserAvailability',
        userId
      });
      return response.success ? response.availabilities : [];
    },

    // Get heatmap data for a class type
    getHeatmap: async (classTypeId) => {
      const response = await callFunction('availabilityManagement', {
        action: 'getAvailabilityHeatmap',
        classTypeId
      });
      return response.success ? response.heatmap : {};
    },

    // Get all students with their availability (admin only)
    getAllStudentsWithAvailability: async () => {
      const response = await callFunction('availabilityManagement', {
        action: 'getAllStudentsWithAvailability'
      });
      return response.success ? response.students : [];
    }
  },

  // Tutor related functions
  tutors: {
    submitApplication: async (applicationData) => {
      const response = await callFunction('utilities', {
        action: 'submitTutorApplication',
        ...applicationData
      });
      return response;
    },

    getById: async (id) => {
      const response = await callFunction('utilities', {
        action: 'getTutorById',
        tutorId: id
      });
      return response.success ? response.tutor : null;
    },

    // Future admin functions
    getApplications: async (status = 'all') => {
      const response = await callFunction('utilities', {
        action: 'getTutorApplications',
        status
      });
      return response.success ? response.applications : [];
    },

    getApplication: async (tutorId) => {
      const response = await callFunction('utilities', {
        action: 'getTutorApplication',
        tutorId
      });
      return response.success ? response.application : null;
    },

    approveApplication: async (tutorId) => {
      const response = await callFunction('utilities', {
        action: 'approveTutorApplication',
        tutorId
      });
      return response;
    },

    rejectApplication: async (tutorId) => {
      const response = await callFunction('utilities', {
        action: 'rejectTutorApplication',
        tutorId
      });
      return response;
    },

    updateStatus: async (tutorId, status) => {
      const response = await callFunction('utilities', {
        action: 'updateTutorStatus',
        tutorId,
        status
      });
      return response;
    },

    updateInternalNotes: async (tutorId, notes) => {
      const response = await callFunction('utilities', {
        action: 'updateTutorInternalNotes',
        tutorId,
        notes
      });
      return response;
    },

    updateProfile: async (tutorId, data) => {
      const response = await callFunction('utilities', {
        action: 'updateTutorProfile',
        tutorId,
        ...data
      });
      return response;
    },

    deactivate: async (tutorId) => {
      const response = await callFunction('utilities', {
        action: 'deactivateTutor',
        tutorId
      });
      return response;
    },

    sendAvailabilityCheckEmails: async () => {
      const response = await callFunction('utilities', {
        action: 'sendTutorAvailabilityCheckEmails'
      });
      return response;
    }
  },

  // Notification related functions
  notifications: {
    sendWelcome: (email, name) =>
      callFunction('notifications', {
        action: 'sendWelcome',
        email,
        name
      }),

    sendClassJoinConfirmation: (userData, classData) =>
      callFunction('notifications', {
        action: 'sendClassJoinConfirmation',
        userName: userData.name,
        userEmail: userData.email,
        userPhone: userData.phone,
        classType: classData.type,
        day: classData.day,
        time: classData.time,
        currentEnrollment: classData.currentEnrollment,
        totalSpots: classData.totalSpots
      }),

    sendAvailabilityConfirmation: (userName, userEmail, classType, availabilities, classTypeId, userPhone, goalData, isTutoringTrial) =>
      callFunction('notifications', {
        action: 'sendAvailabilityConfirmation',
        userName,
        userEmail,
        classType,
        availabilities,
        classTypeId,
        userPhone,
        goalData,
        isTutoringTrial
      })
  },

  // User related functions
  users: {
    register: async (email, name, phone) => {
      console.log('=== api.users.register called ===');
      console.log('email:', email);
      console.log('name:', name);
      console.log('phone:', phone);
      console.log('phone type:', typeof phone);
      console.log('phone === undefined:', phone === undefined);
      console.log('phone === "":', phone === '');
      console.log('phone === null:', phone === null);
      
      const payload = {
        action: 'register',
        email,
        name
      };

      // Only include phone if it's not empty and actually contains a phone number
      // Check for valid phone: must be a non-empty string after trimming
      if (phone && typeof phone === 'string' && phone.trim().length > 0) {
        console.log('Phone is valid, adding to payload:', phone.trim());
        payload.phone = phone.trim();
      } else {
        console.log('Phone is invalid/empty, NOT adding to payload');
      }
      
      console.log('Final payload:', JSON.stringify(payload));
      console.log('=== Calling callFunction ===');

      const response = await callFunction('userAuth', payload);
      return response;
    },
    deleteStudent: async (userId) => {
      const response = await callFunction('userAuth', {
        action: 'deleteStudent',
        userId
      });
      return response;
    }
  }
};